import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { type Control, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { StrongbackFieldsRenderer } from "./StrongbackFieldsRenderer";
import type { StrongbackTypeLocationSchema } from "./schema";
import { createStrongbackSchema, type StrongbackFlow } from "./validation";

export type { StrongbackTypeLocationSchema };

type StrongbackTypeLocationFormProps = {
  defaultValues?: Partial<StrongbackTypeLocationSchema>;
  onSave: (data: StrongbackTypeLocationSchema) => void;
};

export function StrongbackTypeLocationForm({ defaultValues, onSave }: StrongbackTypeLocationFormProps) {
  const repairType = useCUMechanicalJobStore((state) => state.sections.repairType.data.repairType);
  const isTeeFlow = repairType === "tee";
  const is90Flow = repairType === "long_radius";
  const flow: StrongbackFlow = isTeeFlow ? "tee" : is90Flow ? "90" : "other";
  const { measureUnitOptions, flangeNpsOptions } = useConfigOptions();

  const schema = useMemo(() => createStrongbackSchema(flow), [flow]);
  const strongbackNpsOptions = useMemo(() => {
    const baseOptions = flangeNpsOptions ?? [];
    return [{ title: "custom", value: "custom" }, ...baseOptions];
  }, [flangeNpsOptions]);

  const buildStrongbacksDefaults = () => {
    const defaults = defaultValues?.strongbacks ?? [];
    return Array.from({ length: 4 }, (_, index) => {
      const entry = defaults[index] ?? {};
      // Tee flow: strongback 4 (riser) is locked to Bar/0.
      // 90 flow: strongbacks 2 and 4 (top/bottom) are locked to Bar/0.
      const isLocked = (isTeeFlow && index === 3) || (is90Flow && (index === 1 || index === 3));
      return {
        type: isLocked ? "Bar" : (entry.type ?? ""),
        distance: isLocked ? 0 : (entry.distance ?? undefined),
        unit_for_distance: entry.unit_for_distance ?? "Inches",
        flange_nps: entry.flange_nps ?? "",
        flange_rating: entry.flange_rating ?? "",
        custom_diameter: entry.custom_diameter,
        raised_face_od: entry.raised_face_od,
        flange_num_bolt_holes: entry.flange_num_bolt_holes,
        flange_stud_size: entry.flange_stud_size,
        flange_bolt_circle: entry.flange_bolt_circle,
      };
    });
  };

  const form = useForm<StrongbackTypeLocationSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      strongback_header: defaultValues?.strongback_header,
      strongback_riser: defaultValues?.strongback_riser,
      strongback_top: defaultValues?.strongback_top,
      strongback_bottom: defaultValues?.strongback_bottom,
      match_strongback_to_enclosure: defaultValues?.match_strongback_to_enclosure,
      strongbacks: buildStrongbacksDefaults(),
    },
  });

  const { handleSubmit, watch } = form;

  const strongbackHeader = watch("strongback_header");
  const strongbackRiser = watch("strongback_riser");
  const strongbackTop = watch("strongback_top");
  const strongbackBottom = watch("strongback_bottom");

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-4">
        {isTeeFlow ? (
          <>
            {/* Header and Riser selection */}
            <div className="flex flex-wrap items-end gap-2">
              <YesNoSelect control={form.control} name="strongback_header" label="Strongback Header" />
              <YesNoSelect control={form.control} name="strongback_riser" label="Strongback Riser" />
            </div>

            {/* Header strongbacks (1 and 2) */}
            {strongbackHeader && (
              <>
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={1}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                />
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={2}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                />
              </>
            )}

            {/* Riser strongbacks (3 and 4) */}
            {strongbackRiser && (
              <>
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={3}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                />
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={4}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                  locked={true}
                />
              </>
            )}
          </>
        ) : is90Flow ? (
          <>
            {/* Top and Bottom selection */}
            <div className="flex flex-wrap items-end gap-2">
              <YesNoSelect control={form.control} name="strongback_top" label="Strongback Top" />
              <YesNoSelect control={form.control} name="strongback_bottom" label="Strongback Bottom" />
            </div>

            {/* Top strongbacks (1 and 2) */}
            {strongbackTop && (
              <>
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={1}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                />
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={2}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                  locked={true}
                />
              </>
            )}

            {/* Bottom strongbacks (3 and 4) */}
            {strongbackBottom && (
              <>
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={3}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                />
                <StrongbackFieldsRenderer
                  form={form}
                  strongbackNumber={4}
                  showFields={true}
                  measureUnitOptions={measureUnitOptions}
                  npsOptions={strongbackNpsOptions}
                  locked={true}
                />
              </>
            )}
          </>
        ) : (
          <>
            {/* Straight Line and Flange Clamp flows */}
            <StrongbackFieldsRenderer
              form={form}
              strongbackNumber={1}
              showFields={true}
              measureUnitOptions={measureUnitOptions}
              npsOptions={strongbackNpsOptions}
            />
            <StrongbackFieldsRenderer
              form={form}
              strongbackNumber={2}
              showFields={true}
              measureUnitOptions={measureUnitOptions}
              npsOptions={strongbackNpsOptions}
            />
          </>
        )}

        <Button type="submit" className="ml-auto self-end">
          Save
        </Button>
      </form>
    </Form>
  );
}

type YesNoSelectProps = {
  control: Control<StrongbackTypeLocationSchema>;
  name: "strongback_header" | "strongback_riser" | "strongback_top" | "strongback_bottom";
  label: string;
};

function YesNoSelect({ control, name, label }: YesNoSelectProps) {
  return (
    <div className="flex-1 basis-full md:basis-56">
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Select
                value={field.value === undefined ? undefined : field.value ? "yes" : "no"}
                onValueChange={(value) => field.onChange(value === "yes")}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
